import React, { memo, useEffect, useState, useCallback, useRef } from 'react';
import { Avatar, ImageIcon } from '@saleshandy/design-system';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { Images } from '../../../../../../../shared/app-constants';
import Select from '../../../../../../../shared/design-system/components/select';
import { templateTypes } from '../../../utils/helper';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import TemplateHeader from '../../template-header';
import { TemplateListProps } from '../types';
import {
  AiColEnrichmentModalSteps,
  SupportedAiModelsServiceProvider,
} from '../../../enums';
import TemplateSkeleton from '../../template-skeleton';
import {
  OverlayPopover,
  OverlayTooltip,
} from '../../../../../../../shared/design-system/components/overlay';
import {
  Placement,
  TriggerType,
} from '../../../../../../../shared/design-system/components/overlay-taget/enums';
import Tab from '../../../../../../../shared/svg/tab';
import {
  getIsRequestPending,
  executeOnRequestStatus,
} from '../../../../../../../shared/utils';
import { EnrichmentTemplateCategory } from '../../../../../enums/prospect';
import NoResultScreen from '../../no-result-screen';
import {
  setSelectedAiTemplateId,
  setSelectedAiTemplateShareableId,
  resetDeleteAiColumnTemplateRequest,
} from '../../../../../prospect-slice';
import { Trash } from '../../../../../../../shared/svg';
import DeleteTemplateModal from '../../delete-template-modal';
import Input from '../../../../../../../shared/design-system/components/input';
import { InputIconConfig } from '../../../../../../../shared/design-system/components/input/types';
import { RootState } from '../../../../../../../store/root-reducer';
import AiCreditCapsule from '../../ai-credit-capsule/ai-credit-capsule';
import toaster, { Theme } from '../../../../../../../shared/toaster';
import TruncatedTextWithAutoTooltip from '../../../../../../../shared/components/truncated-text-with-auto-tooltip';

const TemplateList: React.FC<TemplateListProps> = memo(
  ({
    handleMoveToNextStep,
    handleModalClose,
    handleBack,
    getTemplateOptionListRequest,
    handleDeleteAiColumnTemplate,
    hasMore,
    templateLists,
    getTemplateRequestStatus,
    deleteTemplateStatus,
    currentStep,
    stepData,
    isAiColumnTemplateListCachedFromFilter,
  }) => {
    const [selectedTemplateKey, setSelectedTemplateKey] = useState('0');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState<any>(null);
    const [dynamicPerPageLimit, setDynamicPerPageLimit] = useState(10);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const dispatch = useDispatch();
    const inputRef = useRef<HTMLInputElement>(null);
    const lastSearchTermRef = useRef<string>('');
    const containerRef = useRef<HTMLDivElement>(null);
    const PER_PAGE_LIMIT = 10;

    const aiIconMap: Partial<
      Record<SupportedAiModelsServiceProvider, string>
    > = {
      [SupportedAiModelsServiceProvider.Anthropic]: Images.Anthropic,
      [SupportedAiModelsServiceProvider.OpenAI]: Images.OpenAI,
      [SupportedAiModelsServiceProvider.Google]: Images.Gemini,
      [SupportedAiModelsServiceProvider.Perplexity]: Images.Perplexity,
    };

    const isTemplateRequestStatusPending = getIsRequestPending(
      getTemplateRequestStatus,
    );

    // Calculate dynamic per page limit based on available height
    const calculateDynamicPerPageLimit = useCallback(() => {
      if (!containerRef.current) return PER_PAGE_LIMIT;

      const containerHeight = containerRef.current.clientHeight;
      const templateItemHeight = 125; // Approximate height of each template item
      const headerHeight = 200; // Approximate height of header, search, and other elements
      const availableHeight = containerHeight - headerHeight;

      // Calculate how many templates can fit in available space
      const templatesThatFit = Math.ceil(availableHeight / templateItemHeight);

      // Ensure we load at least 3 templates and at most 20
      const calculatedLimit = Math.max(3, Math.min(templatesThatFit + 2, 20));

      return Math.ceil(calculatedLimit);
    }, []);

    // Function definitions
    const closeDeleteModal = () => {
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    };

    const handleDeleteTemplate = (templateItem, e) => {
      e.stopPropagation();
      setTemplateToDelete(templateItem);
      setShowDeleteModal(true);
    };

    const confirmDeleteTemplate = useCallback(() => {
      if (templateToDelete?.id) {
        handleDeleteAiColumnTemplate(templateToDelete.id);
      }
    }, [handleDeleteAiColumnTemplate, templateToDelete]);

    const fields = useSelector((state: RootState) => state?.prospect?.fields);

    // Map selected key to API category enum
    const resolveCategory = (
      key: string,
    ): EnrichmentTemplateCategory | undefined => {
      // If key is '0' (All), return undefined to not filter by category
      if (key === '0') return undefined;

      // For enum-based keys, the key itself is the category value
      return key as EnrichmentTemplateCategory;
    };

    const handleCategoryChange = ([option]: any[]) => {
      // Don't make API call if the same option is already selected
      if (selectedTemplateKey === option.key) {
        return;
      }

      setSelectedTemplateKey(option.key);
      setPage(1);

      // Scroll to top when category changes
      const scrollableElement = document.getElementById(
        'scrollable-template-list',
      );
      if (scrollableElement) {
        scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });
      }

      getTemplateOptionListRequest({
        page: 1,
        perPage: dynamicPerPageLimit,
        append: false,
        search: searchTerm,
        category: resolveCategory(option.key),
      });
    };
    // Calculate dynamic per page limit on mount and resize
    useEffect(() => {
      const updatePerPageLimit = () => {
        const newLimit = calculateDynamicPerPageLimit();
        setDynamicPerPageLimit(newLimit);
        setIsInitialLoad(false);
      };

      // Calculate initial limit after a small delay to ensure container is rendered
      const timeoutId = setTimeout(updatePerPageLimit, 100);

      // Add resize listener
      const handleResize = debounce(updatePerPageLimit, 300);
      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', handleResize);
        handleResize.cancel();
      };
    }, [calculateDynamicPerPageLimit]);

    // Initial load - only run once when dynamicPerPageLimit is calculated
    useEffect(() => {
      if (isInitialLoad) return;

      if (
        templateLists?.length > 0 &&
        !isAiColumnTemplateListCachedFromFilter
      ) {
        setPage(Math.ceil(templateLists?.length / dynamicPerPageLimit));
        return;
      }
      setPage(1);
      getTemplateOptionListRequest({
        page: 1,
        perPage: dynamicPerPageLimit,
        search: '',
        category: resolveCategory(selectedTemplateKey),
      });
      lastSearchTermRef.current = '';
    }, [isInitialLoad, dynamicPerPageLimit]);

    // Fetch next page function
    const fetchNextTemplates = useCallback(() => {
      if (!isTemplateRequestStatusPending && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        getTemplateOptionListRequest({
          page: nextPage,
          perPage: dynamicPerPageLimit,
          append: true,
          search: searchTerm,
          category: resolveCategory(selectedTemplateKey),
        });
      }
    }, [
      page,
      hasMore,
      isTemplateRequestStatusPending,
      getTemplateOptionListRequest,
      searchTerm,
      selectedTemplateKey,
      dynamicPerPageLimit,
    ]);

    useEffect(() => {
      executeOnRequestStatus({
        status: deleteTemplateStatus,
        onSuccess: () => {
          closeDeleteModal();
          toaster.success('Template deleted successfully', {
            theme: Theme.New,
          });

          // Reset the delete request state
          dispatch(resetDeleteAiColumnTemplateRequest());
          // Refresh the template list
          getTemplateOptionListRequest({
            page: 1,
            perPage: page * dynamicPerPageLimit,
            append: false,
            search: searchTerm,
            category: resolveCategory(selectedTemplateKey),
          });
        },
      });
    }, [deleteTemplateStatus]);

    const handleSearchInputFocus = () => {
      inputRef.current?.focus();
    };

    // Debounced search function
    const debouncedSearch = useCallback(
      debounce((term: string) => {
        const trimmedTerm = term?.trim();
        if (trimmedTerm === lastSearchTermRef.current) {
          return;
        }
        lastSearchTermRef.current = trimmedTerm;
        setPage(1);
        getTemplateOptionListRequest({
          page: 1,
          perPage: dynamicPerPageLimit,
          append: false,
          search: trimmedTerm,
          category: resolveCategory(selectedTemplateKey),
        });
      }, 500),
      [getTemplateOptionListRequest, selectedTemplateKey, dynamicPerPageLimit],
    );

    // Cleanup debounced function on unmount
    useEffect(
      () => () => {
        debouncedSearch.cancel();
      },
      [debouncedSearch],
    );

    const handleClearSearch = () => {
      setSearchTerm('');
      setPage(1);
      lastSearchTermRef.current = '';
      // Scroll to top when category changes
      const scrollableElement = document.getElementById(
        'scrollable-template-list',
      );
      if (scrollableElement) {
        scrollableElement.scrollTo({ top: 0, behavior: 'smooth' });
      }

      getTemplateOptionListRequest({
        page: 1,
        perPage: dynamicPerPageLimit,
        append: false,
        search: '',
        category: resolveCategory(selectedTemplateKey),
      });
    };

    // Configure search icons based on search term
    const getSearchIcons = (): InputIconConfig[] => {
      const icons: InputIconConfig[] = [
        {
          place: Input.IconPlace.Left,
          identifier: 'search',
        },
      ];

      if (searchTerm && searchTerm.length > 0) {
        icons.push({
          place: Input.IconPlace.Right,
          identifier: 'close-o cursor-pointer',
          onClick: handleClearSearch,
        });
      }

      return icons;
    };

    const handleTemplateClick = (templateItem) => {
      // Check if we came from SMART_FILL step
      const sourceStep = stepData?.sourceStep;

      if (sourceStep === AiColEnrichmentModalSteps.EXISTING_COLUMN) {
        // If coming from SMART_FILL, go back to SMART_FILL with template data
        handleMoveToNextStep(AiColEnrichmentModalSteps.EXISTING_COLUMN, {
          templateId: templateItem?.id,
          shareableId: templateItem?.shareableId,
          fromTemplateList: true, // Flag to indicate we came from template selection
        });
      } else {
        // Normal flow - go to template detail
        handleMoveToNextStep(AiColEnrichmentModalSteps.TEMPLATE_DETAIL, {
          templateId: templateItem?.id,
          shareableId: templateItem?.shareableId,
        });
      }

      dispatch(setSelectedAiTemplateId(templateItem?.id));
      dispatch(setSelectedAiTemplateShareableId(templateItem?.shareableId));
    };

    const visibleTags = templateLists?.map((item) =>
      item?.mergeTags?.slice(0, 3),
    );
    const hiddenTags = templateLists?.map((item) => item?.mergeTags?.slice(3));

    const hasNoTemplate = templateLists?.length === 0;
    return (
      <>
        <TemplateHeader
          title="Use Template to Enrich Prospect"
          onClose={handleModalClose}
          handleMoveBack={handleBack}
          currentStep={currentStep}
        />
        <div className="search-dropdown">
          <div
            className="search-input-container"
            {...accessibleOnClick(handleSearchInputFocus)}
          >
            <Input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value);
                debouncedSearch(value);
              }}
              className="search-input"
              icons={getSearchIcons()}
              ref={inputRef}
            />
          </div>

          <div className="vertical-divider" />
          <Select
            options={templateTypes}
            className="template-select"
            optionRenderer={(option) => <span>{option?.label}</span>}
            selectedOptionRenderer={([option]) => (
              <div className="d-flex align-items-center gap-1">
                <Tab />
                <span>{option?.label}</span>
              </div>
            )}
            selectedOptionKey={selectedTemplateKey}
            onChange={handleCategoryChange}
          />
        </div>

        <div
          ref={containerRef}
          id="scrollable-template-list"
          className={`${
            hasNoTemplate && !isTemplateRequestStatusPending
              ? 'no-template-active'
              : ''
          }`}
        >
          <InfiniteScroll
            dataLength={templateLists?.length || 0}
            next={fetchNextTemplates}
            hasMore={hasMore}
            loader={
              <div className="d-flex justify-content-center align-items-center py-3">
                <Spinner animation="border" variant="primary" />
              </div>
            }
            endMessage={
              templateLists?.length > 0 ? (
                <div className="d-flex justify-content-center align-items-center my-2">
                  <p className="text-muted ai-col-enrichment-no-more-templates">
                    All templates have been displayed.
                  </p>
                </div>
              ) : (
                <span />
              )
            }
            scrollableTarget="scrollable-template-list"
          >
            {isTemplateRequestStatusPending && page === 1 ? (
              <TemplateSkeleton count={8} />
            ) : (
              <>
                <div className="template-container">
                  {templateLists?.map((item, index) => (
                    <div
                      className="template"
                      key={`template-${item?.id || index + 1}`}
                      {...accessibleOnClick(() => handleTemplateClick(item))}
                    >
                      <div className="template-header">
                        <div className="ai-title">
                          <TruncatedTextWithAutoTooltip
                            text={item?.name}
                            className="template-title"
                          />
                          <ImageIcon
                            src={
                              aiIconMap[item?.serviceProvider?.toLowerCase()]
                            }
                            alt="ai-icon"
                            width={16}
                            height={16}
                          />
                        </div>
                        <div className="web-searched-credits-container">
                          {item?.category === 'userDefined' && (
                            <OverlayTooltip
                              text={`${item?.createdByUser?.firstName} ${item?.createdByUser?.lastName}`}
                            >
                              <Avatar
                                firstName={item?.createdByUser?.firstName || ''}
                                lastName={item?.createdByUser?.lastName || ''}
                              />
                            </OverlayTooltip>
                          )}
                          <div className="web-searched-credits">
                            {item?.tools?.includes('web_search_context') && (
                              <OverlayTooltip text="Web Search Included">
                                <ImageIcon
                                  src={Images?.GlobeBlue}
                                  alt="web-enabled"
                                />
                              </OverlayTooltip>
                            )}
                            <AiCreditCapsule
                              credits={String(item?.creditUsagePerRow || 1)}
                              showBorder
                            />
                          </div>
                          {item?.category === 'userDefined' && (
                            <OverlayTooltip text="Delete Template">
                              <div
                                className="delete-icon-wrapper"
                                {...accessibleOnClick((e) =>
                                  handleDeleteTemplate(item, e),
                                )}
                              >
                                <Trash
                                  className="delete-icon"
                                  width={16}
                                  height={16}
                                />
                              </div>
                            </OverlayTooltip>
                          )}
                        </div>
                      </div>
                      <div className="template-body">{item?.description}</div>
                      <div className="properties-capsule">
                        <div className="tags">
                          {visibleTags[index]?.map((tag, indexs) => (
                            <OverlayTooltip
                              text={tag}
                              key={`visibleTags-${indexs + 1}`}
                            >
                              <span key={`tag-${indexs + 1}`} className="tag">
                                {tag}
                              </span>
                            </OverlayTooltip>
                          ))}
                          {hiddenTags[index]?.length > 0 && (
                            <OverlayPopover
                              className="hidden-tags-popover"
                              content={
                                <div
                                  className="hidden-tags"
                                  {...accessibleOnClick((e) =>
                                    e.stopPropagation(),
                                  )}
                                >
                                  {hiddenTags[index]?.map((tag, indexs) => (
                                    <div className="tags-star-icon">
                                      <span
                                        className="tag-popover"
                                        key={`hidden-tag-${indexs + 1}`}
                                      >
                                        {tag}
                                      </span>
                                      {fields?.find(
                                        (f) => f?.label === tag.toString(),
                                      )?.isAiGenerated && (
                                        <ImageIcon
                                          src={Images.NoResultGradient}
                                          alt="ai-icon"
                                          width={16}
                                          height={16}
                                        />
                                      )}
                                    </div>
                                  ))}
                                </div>
                              }
                              placement={Placement.Bottom}
                              trigger={TriggerType.Hover}
                            >
                              <span
                                className="more-tags"
                                {...accessibleOnClick((e) =>
                                  e.stopPropagation(),
                                )}
                              >
                                +{hiddenTags[index]?.length}
                              </span>
                            </OverlayPopover>
                          )}
                        </div>
                        <div className="arrow-gradient">
                          <ImageIcon
                            src={Images?.ArrowRightGradient}
                            alt="arrow-right"
                            className="right-arrow"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {templateLists?.length === 0 && <NoResultScreen />}
                </div>
              </>
            )}
          </InfiniteScroll>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <DeleteTemplateModal
            show={showDeleteModal}
            onClose={closeDeleteModal}
            onDelete={confirmDeleteTemplate}
            isRequestPending={deleteTemplateStatus === 'pending'}
            templateName={templateToDelete?.name}
          />
        )}
      </>
    );
  },
);

export default TemplateList;
